import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Checkbox from "@mui/material/Checkbox";
import {
  Document,
  Packer,
  Paragraph,
  ImageRun,
  HeadingLevel,
  AlignmentType,
} from "docx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  getQuestions,
  createQuestions,
  checkDuplicateQuestions,
  updateQuestion,
  deleteQuestion,
  getAllTags,
} from "../../services/QuestionBankService";
import DeleteConfirmModal from "../common/DeleteConfirmModal";
import MathRendererContent from "../../common/MathRendererContent";
import mammoth from "mammoth";
import UploadService from "../../services/UploadService";
import { HOSTNAME } from "../../common/apiClient";
import {
  isLineQuestionAnswer,
  regexQuestionAnswer,
  removeHTMLTags,
  toUpperCaseNonAccentVietnamese,
} from "../../common/Utils";

const QuestionBankManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // chỉ update khi Enter
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openBulkPreviewDialog, setOpenBulkPreviewDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [formData, setFormData] = useState({
    contentQuestions: "",
    imageUrl: "",
    explanation: "",
    tags: [],
  });
  const fileInputRef = useRef(null);
  const imageUploadRef = useRef(null);
  const tempImageDataRef = useRef(new Map());
  const currentEditingContentRef = useRef("");
  const currentEditingExplanationRef = useRef("");
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchQuestions();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery, selectedTags]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const tagsQuery = selectedTags.length > 0 ? selectedTags.join(",") : "";
      const response = await getQuestions(
        page + 1,
        rowsPerPage,
        searchQuery,
        tagsQuery
      );
      if (response && response.data) {
        setQuestions(response.data);
        setTotalQuestions(response.totalQuestions || 0);
      }
    } catch (error) {
      toast.error("Lỗi khi tải danh sách câu hỏi");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await getAllTags();
      if (response && response.data) {
        setAllTags(response.data);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const submitSearch = () => {
    setPage(0);
    setSearchQuery(searchInput.trim());
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitSearch();
    }
  };
  // Xử lý file Word - đơn giản hóa từ processQuestionsFromDocx
  async function processQuestionsFromDocx(file) {
    try {
      tempImageDataRef.current.clear();

      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "b => strong",
            "i => em",
            "u => u",
            "strike => del",
            "sub => sub",
            "sup => sup",
          ],
          includeDefaultStyleMap: true,
          includeEmbeddedStyleMap: true,
          convertImage: mammoth.images.imgElement(async function (image) {
            try {
              const imageBuffer = await image.read();
              const imageKey = `temp-image-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`;

              tempImageDataRef.current.set(imageKey, {
                buffer: imageBuffer,
                contentType: image.contentType,
                altText: image.altText || "Image from Word document",
                originalName: `word-image-${Date.now()}.${
                  image.contentType.split("/")[1]
                }`,
              });

              // Convert buffer to base64 in browser
              const uint8Array = new Uint8Array(imageBuffer);
              let binary = "";
              for (let i = 0; i < uint8Array.length; i++) {
                binary += String.fromCharCode(uint8Array[i]);
              }
              const base64 = `data:${image.contentType};base64,${btoa(binary)}`;

              return {
                src: base64,
                alt: image.altText || "Image from Word document",
                "data-temp-key": imageKey,
                class: "mx-auto",
                style: "",
              };
            } catch (error) {
              console.error("Error processing image:", error);
              return { src: "", alt: "Error loading image" };
            }
          }),
        }
      );

      const htmlContent = result.value;
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const allContent = doc.body.innerHTML;

      const lines = allContent
        .split(/<\/p>|<br\s*\/?>/)
        .map((line) =>
          line
            .replace(/<p[^>]*>/g, "")
            .replaceAll("&nbsp;", " ")
            .replaceAll("&lt;", "<")
            .replaceAll("&gt;", ">")
            .replaceAll("&amp;", "&")
            .trim()
        )
        .map((line) => {
          if (isLineQuestionAnswer(line)) {
            return removeHTMLTags(line);
          }
          return line;
        })
        .filter((line) => line && line !== "" && line.length > 0);

      let questionsArray = [];
      let currentQuestion = null;

      const cleanContent = (content) => {
        if (!content) return content;
        content = content.replace(/^(\s*<br\s*\/?>)+/gi, "");
        content = content.replace(/(\s*<br\s*\/?>)+$/gi, "");
        content = content.replace(/(<br\s*\/?>){3,}/gi, "<br/><br/>");
        return content.trim();
      };

      for (let i = 0; i < lines.length; i++) {
        let line = lines[i].trim() || "";
        console.log("🚀 ~ line:", line);
        if (!line || line.length === 0) continue;

        const questionMatch = regexQuestionAnswer(line);

        if (questionMatch) {
          if (currentQuestion) {
            questionsArray.push(currentQuestion);
          }

          const questionContent = questionMatch[2];

          currentQuestion = {
            contentQuestions: cleanContent(questionContent),
            tags: [],
            explanation: "",
          };

          for (let j = i + 1; j < lines.length; j++) {
            let nextLine = lines[j].trim() || "";

            if (
              regexQuestionAnswer(nextLine) ||
              nextLine.toLocaleLowerCase().startsWith("tag") ||
              nextLine.toLocaleLowerCase().startsWith("lời giải")
            )
              break;

            if (nextLine && nextLine.length > 0) {
              currentQuestion.contentQuestions += "<br/>" + nextLine;
            }
          }
        } else if (
          line.toLocaleLowerCase().startsWith("tag") &&
          currentQuestion
        ) {
          currentQuestion.tags = line
            .toLocaleLowerCase()
            .split("tag:")[1]
            ?.split("|")
            ?.map((tag) => toUpperCaseNonAccentVietnamese(tag.trim()));
        } else if (
          line.toLocaleLowerCase().startsWith("lời giải") &&
          currentQuestion
        ) {
          // Trường hợp 1: Explanation cùng dòng
          const explanationMatch = line.split(/Lời giải:?/)[1]?.trim() || "";
          currentQuestion.explanation = cleanContent(explanationMatch.trim());

          // Tiếp tục đọc các dòng tiếp theo để lấy phần explanation còn lại
          for (let j = i + 1; j < lines.length; j++) {
            const nextLine = lines[j].trim();

            // Nếu gặp câu hỏi tiếp theo thì dừng
            if (
              regexQuestionAnswer(nextLine) ||
              nextLine.toLocaleLowerCase().startsWith("tag") ||
              nextLine.toLocaleLowerCase().startsWith("lời giải")
            ) {
              break;
            }

            // Nếu có nội dung explanation tiếp theo
            if (nextLine && nextLine.length > 0) {
              currentQuestion.explanation += "<br/>" + cleanContent(nextLine);
            }
          }
        }
      }

      if (currentQuestion) {
        questionsArray.push(currentQuestion);
      }

      console.log(
        "🚀 ~ processQuestionsFromDocx ~ questionsArray:",
        questionsArray
      );
      return questionsArray;
    } catch (error) {
      console.error("Error processing docx file:", error);
      throw error;
    }
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".docx")) {
      toast.error("Chỉ hỗ trợ file .docx");
      fileInputRef.current.value = null;
      return;
    }

    try {
      setLoading(true);
      toast.info("Đang xử lý file...");

      const questionsData = await processQuestionsFromDocx(file);

      if (questionsData.length === 0) {
        toast.error("Không tìm thấy câu hỏi nào trong file");
        fileInputRef.current.value = null;
        return;
      }

      // Lưu parsed questions và mở dialog chỉnh sửa
      setParsedQuestions(questionsData);
      setSelectedQuestionIndex(0);
      setOpenBulkDialog(true);
      fileInputRef.current.value = null;
    } catch (error) {
      console.error("Error uploading file:", error);
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi xử lý file");
      fileInputRef.current.value = null;
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...parsedQuestions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setParsedQuestions(updated);
  };

  const handleSelectQuestion = (index) => {
    // Lưu nội dung đang chỉnh sửa trước khi chuyển
    if (
      selectedQuestionIndex >= 0 &&
      selectedQuestionIndex < parsedQuestions.length
    ) {
      if (currentEditingContentRef.current !== undefined) {
        handleQuestionChange(
          selectedQuestionIndex,
          "contentQuestions",
          currentEditingContentRef.current
        );
      }
      if (currentEditingExplanationRef.current !== undefined) {
        handleQuestionChange(
          selectedQuestionIndex,
          "explanation",
          currentEditingExplanationRef.current
        );
      }
    }
    setSelectedQuestionIndex(index);
    currentEditingContentRef.current =
      parsedQuestions[index].contentQuestions || "";
    currentEditingExplanationRef.current =
      parsedQuestions[index].explanation || "";
  };

  const uploadBase64ImagesInContent = async (content) => {
    if (!content) return content;

    const base64Regex =
      /<img[^>]*src="data:image\/([^;]+);base64,([^"]+)"[^>]*>/gi;
    const matches = [...content.matchAll(base64Regex)];

    if (matches.length === 0) return content;

    let updatedContent = content;

    for (const match of matches) {
      try {
        const mimeType = match[1];
        const base64Data = match[2];

        // Convert base64 to blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${mimeType}` });

        const file = new File([blob], `image-${Date.now()}.${mimeType}`, {
          type: `image/${mimeType}`,
        });

        const uploadResponse = await UploadService.uploadQuestionImage(file);
        if (uploadResponse?.data?.imageUrl) {
          const serverImageUrl = `${HOSTNAME}${uploadResponse.data.imageUrl}`;
          updatedContent = updatedContent.replace(match[0], (imgTag) =>
            imgTag.replace(/src="data:[^"]+"/, `src="${serverImageUrl}"`)
          );
        }
      } catch (error) {
        console.error("Error uploading base64 image:", error);
      }
    }

    return updatedContent;
  };

  const handleSubmitBulk = async () => {
    if (parsedQuestions.length === 0) {
      toast.warning("Chưa có câu hỏi để upload");
      return;
    }

    // Commit current editing content và explanation vào state trước khi submit
    if (
      selectedQuestionIndex >= 0 &&
      selectedQuestionIndex < parsedQuestions.length
    ) {
      if (currentEditingContentRef.current !== undefined) {
        handleQuestionChange(
          selectedQuestionIndex,
          "contentQuestions",
          currentEditingContentRef.current
        );
      }
      if (currentEditingExplanationRef.current !== undefined) {
        handleQuestionChange(
          selectedQuestionIndex,
          "explanation",
          currentEditingExplanationRef.current
        );
      }
    }

    // Validate
    const invalidQuestions = parsedQuestions.filter(
      (q) => !q.contentQuestions || q.contentQuestions.trim() === ""
    );

    if (invalidQuestions.length > 0) {
      toast.error(
        `Có ${invalidQuestions.length} câu hỏi thiếu nội dung. Vui lòng kiểm tra lại.`
      );
      return;
    }

    setLoading(true);

    try {
      // Bước 1: Check duplicate trước
      toast.info("Đang kiểm tra trùng lặp...");
      const duplicateCheckResponse = await checkDuplicateQuestions(
        parsedQuestions
      );
      console.log(
        "🚀 ~ handleSubmitBulk ~ duplicateCheckResponse:",
        duplicateCheckResponse
      );

      if (!duplicateCheckResponse) {
        throw new Error("Lỗi khi kiểm tra trùng lặp");
      }

      const { uniqueIndices, uniqueCount, duplicateCount } =
        duplicateCheckResponse;

      if (uniqueCount === 0) {
        toast.warning(
          "Tất cả câu hỏi đều trùng lặp. Không có câu hỏi nào được tạo."
        );
        setOpenBulkDialog(false);
        setParsedQuestions([]);
        setSelectedQuestionIndex(0);
        fetchQuestions();
        fetchTags();
        return;
      }

      if (duplicateCount > 0) {
        toast.warning(`Có ${duplicateCount} câu hỏi trùng lặp sẽ bị bỏ qua.`);
      }

      // Bước 2: Chỉ lấy các câu hỏi không trùng
      const uniqueQuestions = parsedQuestions.filter((_, index) =>
        uniqueIndices.includes(index)
      );

      // Bước 3: Chỉ upload ảnh cho các câu hỏi không trùng
      const tempImageData = tempImageDataRef.current;
      let finalQuestions = [...uniqueQuestions];

      if (tempImageData && tempImageData.size > 0) {
        // Tìm các imageKey được sử dụng trong các câu hỏi không trùng
        const imageKeysInUniqueQuestions = new Set();
        uniqueQuestions.forEach((question) => {
          const content = question.contentQuestions || "";
          // Tìm tất cả data-temp-key trong content
          const tempKeyMatches = content.matchAll(/data-temp-key="([^"]+)"/gi);
          for (const match of tempKeyMatches) {
            imageKeysInUniqueQuestions.add(match[1]);
          }
        });

        // Chỉ upload những ảnh thuộc về câu hỏi không trùng
        const imagesToUpload = Array.from(tempImageData.entries()).filter(
          ([imageKey]) => imageKeysInUniqueQuestions.has(imageKey)
        );

        if (imagesToUpload.length > 0) {
          toast.info(
            `Đang upload ${imagesToUpload.length} ảnh cho ${uniqueCount} câu hỏi...`
          );

          const uploadPromises = [];
          for (const [imageKey, imageData] of imagesToUpload) {
            const uploadPromise = (async () => {
              try {
                const imageFile = new File(
                  [imageData.buffer],
                  imageData.originalName,
                  { type: imageData.contentType }
                );

                const uploadResponse = await UploadService.uploadQuestionImage(
                  imageFile
                );

                if (uploadResponse?.data?.imageUrl) {
                  const serverImageUrl = `${HOSTNAME}${uploadResponse.data.imageUrl}`;
                  return { imageKey, serverImageUrl, success: true };
                }
                return { imageKey, serverImageUrl: null, success: false };
              } catch (error) {
                console.error("Error uploading image:", imageKey, error);
                return { imageKey, serverImageUrl: null, success: false };
              }
            })();
            uploadPromises.push(uploadPromise);
          }

          const uploadResults = await Promise.all(uploadPromises);
          const imageUrlMapping = new Map();

          uploadResults.forEach((result) => {
            if (result.success) {
              imageUrlMapping.set(result.imageKey, result.serverImageUrl);
            }
          });

          // Replace base64 with server URLs (chỉ cho câu hỏi không trùng)
          finalQuestions = finalQuestions.map((question) => {
            let updatedContent = question.contentQuestions || "";

            imageUrlMapping.forEach((serverUrl, tempKey) => {
              const imgRegex = new RegExp(
                `<img([^>]*?)src="data:[^"]*"([^>]*?)data-temp-key="${tempKey}"([^>]*?)>`,
                "gi"
              );
              updatedContent = updatedContent.replace(
                imgRegex,
                `<img$1src="${serverUrl}"$2data-uploaded="true"$3>`
              );
            });

            return { ...question, contentQuestions: updatedContent };
          });
        }

        // Clear tempImageData sau khi upload xong
        tempImageDataRef.current.clear();
      }

      // Bước 4: Upload pasted base64 images (chỉ cho câu hỏi không trùng)
      finalQuestions = await Promise.all(
        finalQuestions.map(async (question) => {
          const updatedContentQuestions = await uploadBase64ImagesInContent(
            question.contentQuestions
          );
          const updatedExplanation = await uploadBase64ImagesInContent(
            question.explanation
          );

          return {
            ...question,
            contentQuestions: updatedContentQuestions,
            explanation: updatedExplanation,
          };
        })
      );

      // Bước 5: Bulk create questions (chỉ các câu hỏi không trùng)
      const response = await createQuestions(finalQuestions);
      toast.success(response.message);

      setOpenBulkDialog(false);
      setParsedQuestions([]);
      setSelectedQuestionIndex(0);
      fetchQuestions();
      fetchTags();
    } catch (error) {
      console.error("Error submitting bulk questions:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Lỗi khi tạo câu hỏi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (question = null) => {
    if (question) {
      setSelectedQuestion(question);
      setFormData({
        contentQuestions: question.contentQuestions || "",
        imageUrl: question.imageUrl || "",
        explanation: question.explanation || "",
        tags: question.tags || [],
      });
    } else {
      setSelectedQuestion(null);
      setFormData({
        contentQuestions: "",
        imageUrl: "",
        explanation: "",
        tags: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuestion(null);
    setFormData({
      contentQuestions: "",
      imageUrl: "",
      explanation: "",
      tags: [],
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Chỉ hỗ trợ file ảnh");
      imageUploadRef.current.value = null;
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file không được vượt quá 5MB");
      imageUploadRef.current.value = null;
      return;
    }

    try {
      setUploadingImage(true);
      toast.info("Đang upload ảnh...");

      const uploadResponse = await UploadService.uploadQuestionImage(file);

      if (uploadResponse?.data?.imageUrl) {
        const serverImageUrl = `${HOSTNAME}${uploadResponse.data.imageUrl}`;
        setFormData((prev) => ({
          ...prev,
          imageUrl: serverImageUrl,
        }));
        toast.success("Upload ảnh thành công");
      } else {
        toast.error("Upload ảnh thất bại");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      const message = error?.response?.data?.message || "Lỗi khi upload ảnh";
      toast.error(message);
    } finally {
      setUploadingImage(false);
      imageUploadRef.current.value = null;
    }
  };

  const handleTagsChange = (event, newValue) => {
    console.log("🚀 ~ handleTagsChange ~ newValue:", newValue);
    setFormData((prev) => ({
      ...prev,
      tags: newValue.map((tag) => toUpperCaseNonAccentVietnamese(tag)),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.contentQuestions) {
      toast.error("Vui lòng điền nội dung câu hỏi");
      return;
    }

    try {
      setLoading(true);
      if (selectedQuestion) {
        await updateQuestion(selectedQuestion._id, formData);
        toast.success("Cập nhật câu hỏi thành công");
      }
      handleCloseDialog();
      fetchQuestions();
      fetchTags();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (question) => {
    console.log("🚀 ~ handleDeleteClick ~ question:", question);
    setSelectedQuestion(question);
    setOpenDeleteModal(true);
  };

  const handleViewClick = (question) => {
    setViewingQuestion(question);
    setOpenViewDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);

      // Xóa nhiều câu hỏi nếu có selectedQuestions
      if (selectedQuestions.length > 0) {
        const deletePromises = selectedQuestions.map((q) =>
          deleteQuestion(q._id)
        );
        await Promise.all(deletePromises);
        toast.success(`Xóa thành công ${selectedQuestions.length} câu hỏi`);
        setSelectedQuestions([]);
      } else if (selectedQuestion) {
        // Xóa một câu hỏi
        await deleteQuestion(selectedQuestion._id);
        toast.success("Xóa câu hỏi thành công");
      }

      setOpenDeleteModal(false);
      setSelectedQuestion(null);
      fetchQuestions();
      fetchTags();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Có lỗi xảy ra khi xóa câu hỏi");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedQuestions([...questions]);
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleToggleQuestionSelection = (question, event) => {
    if (event.target.checked) {
      setSelectedQuestions([...selectedQuestions, question]);
    } else {
      setSelectedQuestions(
        selectedQuestions.filter((q) => q._id !== question._id)
      );
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedQuestions.length === 0) {
      toast.warning("Vui lòng chọn ít nhất một câu hỏi để xóa");
      return;
    }
    setSelectedQuestion(null);
    setOpenDeleteModal(true);
  };

  const isSelected = (question) => {
    return selectedQuestions.some((q) => q._id === question._id);
  };

  const isAllSelected = () => {
    return (
      questions.length > 0 && selectedQuestions.length === questions.length
    );
  };

  // Convert HTML to plain text (remove LaTeX tags but keep content)
  const htmlToPlainText = (html) => {
    if (!html) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  // Convert HTML content to docx elements (preserving image positions)
  const htmlToDocxElements = async (html) => {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const elements = [];

    // Process all nodes in order
    const processNode = async (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          elements.push(
            new Paragraph({
              text: text,
              spacing: { after: 100 },
            })
          );
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();

        if (tagName === "img") {
          const src = node.getAttribute("src");
          if (src) {
            let imageInput;
            if (src.startsWith("data:")) {
              // Base64 image
              const base64 = src.split(",")[1];
              const mimeType = src.split(",")[0].split(":")[1].split(";")[0];
              imageInput = { base64, isBase64: true, mimeType };
            } else {
              // URL image
              const fullUrl = src.startsWith("http")
                ? src
                : `${HOSTNAME}${src}`;
              imageInput = fullUrl;
            }

            const imageRun = await createImageRun(imageInput);
            if (imageRun) {
              elements.push(
                new Paragraph({
                  children: [imageRun],
                  alignment: AlignmentType.LEFT,
                  spacing: { after: 200 },
                })
              );
            }
          }
        } else if (tagName === "p" || tagName === "div") {
          // Process children of paragraph/div
          for (const child of Array.from(node.childNodes)) {
            await processNode(child);
          }
        } else {
          // For other elements, process their children
          for (const child of Array.from(node.childNodes)) {
            await processNode(child);
          }
        }
      }
    };

    // Process all nodes in body
    for (const node of Array.from(doc.body.childNodes)) {
      await processNode(node);
    }

    return elements;
  };

  const blobToUint8Array = async (blob) => {
    const ab = await blob.arrayBuffer();
    return new Uint8Array(ab);
  };

  const getImageInfo = async (imageUrlOrBase64Obj) => {
    // Case base64 object: { base64, isBase64: true, mimeType?: "image/png" }
    if (
      typeof imageUrlOrBase64Obj === "object" &&
      imageUrlOrBase64Obj.isBase64
    ) {
      const mime = imageUrlOrBase64Obj.mimeType || "image/png";
      const byteChars = atob(imageUrlOrBase64Obj.base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        bytes[i] = byteChars.charCodeAt(i);

      const blob = new Blob([bytes], { type: mime });
      const bitmap = await createImageBitmap(blob);
      return { data: bytes, width: bitmap.width, height: bitmap.height };
    }

    // Case URL
    const res = await fetch(imageUrlOrBase64Obj);
    const blob = await res.blob();
    const bitmap = await createImageBitmap(blob);
    const data = await blobToUint8Array(blob);
    return { data, width: bitmap.width, height: bitmap.height };
  };

  // Convert image URL to ImageRun for docx
  const createImageRun = async (imageInput, maxWidth = 600) => {
    try {
      const { data, width: w0, height: h0 } = await getImageInfo(imageInput);

      if (!data || !w0 || !h0) return null;

      // scale <= 1 để KHÔNG upscale (tránh vỡ)
      const scale = Math.min(1, maxWidth / w0);
      const width = Math.round(w0 * scale);
      const height = Math.round(h0 * scale);
      console.log(
        "🚀 ~ createImageRun ~ data, width: w0, height: h0:",
        data,
        width,
        height
      );
      return new ImageRun({
        data, // dùng Uint8Array luôn, khỏi phải base64
        transformation: { width, height },
      });
    } catch (e) {
      console.error("Error creating image run:", e);
      return null;
    }
  };

  // Download selected questions as Word file
  const handleDownloadWord = async () => {
    if (selectedQuestions.length === 0) {
      toast.warning("Vui lòng chọn ít nhất một câu hỏi để tải xuống");
      return;
    }

    try {
      setLoading(true);
      toast.info(
        `Đang tạo file Word cho ${selectedQuestions.length} câu hỏi...`
      );

      const children = [];

      // Add title
      children.push(
        new Paragraph({
          text: "NGÂN HÀNG CÂU HỎI",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Process each question
      for (let i = 0; i < selectedQuestions.length; i++) {
        const question = selectedQuestions[i];
        const questionNumber = i + 1;

        // Question number and content
        children.push(
          new Paragraph({
            text: `Câu ${questionNumber}:`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        // Add imageUrl if exists (separate field) - add at the beginning
        if (question.imageUrl) {
          const fullImageUrl = question.imageUrl.startsWith("http")
            ? question.imageUrl
            : `${HOSTNAME}${question.imageUrl}`;
          const imageRun = await createImageRun(fullImageUrl);
          if (imageRun) {
            children.push(
              new Paragraph({
                children: [imageRun],
                alignment: AlignmentType.LEFT,
                spacing: { after: 200 },
              })
            );
          }
        }

        // Convert HTML content to docx elements (preserving image positions)
        const contentElements = await htmlToDocxElements(
          question.contentQuestions
        );
        children.push(...contentElements);

        // Tags
        if (question.tags && question.tags.length > 0) {
          children.push(
            new Paragraph({
              text: `Tags: ${question.tags.join("|")}`,
              spacing: { after: 200 },
            })
          );
        }

        // Explanation
        if (question.explanation) {
          children.push(
            new Paragraph({
              text: "Lời giải:",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 200 },
            })
          );

          // Convert HTML explanation to docx elements (preserving image positions)
          const explanationElements = await htmlToDocxElements(
            question.explanation
          );
          if (explanationElements.length > 0) {
            children.push(...explanationElements);
          } else {
            // Fallback to plain text if no elements
            const explanationText = htmlToPlainText(question.explanation);
            if (explanationText.trim()) {
              children.push(
                new Paragraph({
                  text: explanationText,
                  spacing: { after: 400 },
                })
              );
            }
          }
        }

        // Add page break between questions (except last one)
        if (i < selectedQuestions.length - 1) {
          children.push(
            new Paragraph({
              text: "",
              pageBreakBefore: true,
            })
          );
        }
      }

      // Create document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: children,
          },
        ],
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      const fileName = `Cau-hoi-${new Date().getTime()}.docx`;
      saveAs(blob, fileName);

      toast.success(
        `Đã tải xuống ${selectedQuestions.length} câu hỏi thành công!`
      );
    } catch (error) {
      console.error("Error downloading Word file:", error);
      toast.error("Có lỗi xảy ra khi tạo file Word");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a202c" }}>
          Ngân hàng câu hỏi
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {selectedQuestions.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadWord}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Download Word ({selectedQuestions.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteMultiple}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Xóa ({selectedQuestions.length})
              </Button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Upload Word
          </Button>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ overflow: "hidden" }}>
        <Box sx={{ padding: 3 }}>
          <Box sx={{ display: "flex", gap: 2, marginBottom: 3 }}>
            <TextField
              fullWidth
              placeholder="Tìm kiếm câu hỏi..."
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={submitSearch}
                      edge="start"
                      size="small"
                      sx={{ mr: 0.5 }}
                      aria-label="search"
                    >
                      <SearchIcon sx={{ color: "#666" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              options={allTags}
              value={selectedTags}
              onChange={(event, newValue) => {
                setSelectedTags(newValue);
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Lọc theo tags"
                  sx={{ minWidth: 250 }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
            />
          </Box>

          {loading && questions.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 8,
              }}
            >
              <CircularProgress />
            </Box>
          ) : questions.length === 0 ? (
            <Alert severity="info">Không tìm thấy câu hỏi nào</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f8f9fa",
                      "& th": {
                        fontWeight: 700,
                        color: "#1a202c",
                        borderBottom: "2px solid #e2e8f0",
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedQuestions.length > 0 &&
                          selectedQuestions.length < questions.length
                        }
                        checked={isAllSelected()}
                        onChange={handleSelectAll}
                        sx={{ color: "#1976d2" }}
                      />
                    </TableCell>
                    <TableCell>STT</TableCell>
                    <TableCell>Nội dung câu hỏi</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Ngày tạo</TableCell>
                    <TableCell align="center">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((question, index) => {
                    const isItemSelected = isSelected(question);
                    return (
                      <TableRow
                        key={question._id}
                        selected={isItemSelected}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                          "& td": {
                            borderBottom: "1px solid #e2e8f0",
                          },
                          backgroundColor: isItemSelected
                            ? "rgba(25, 118, 210, 0.08)"
                            : "transparent",
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={(e) =>
                              handleToggleQuestionSelection(question, e)
                            }
                            sx={{ color: "#1976d2" }}
                          />
                        </TableCell>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ maxWidth: 500 }}>
                          <Box
                            sx={{
                              maxHeight: "120px",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: "vertical",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <MathRendererContent
                              content={question.contentQuestions}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {question.tags && question.tags.length > 0 ? (
                              question.tags
                                .slice(0, 3)
                                .map((tag, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tag}
                                    size="small"
                                    sx={{ fontSize: "0.75rem" }}
                                  />
                                ))
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {question.createdAt
                            ? new Date(question.createdAt).toLocaleDateString(
                                "vi-VN"
                              )
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Xem chi tiết">
                            <IconButton
                              onClick={() => handleViewClick(question)}
                              sx={{
                                color: "#10b981",
                                marginRight: 1,
                                "&:hover": {
                                  backgroundColor: "rgba(16, 185, 129, 0.1)",
                                },
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Chỉnh sửa">
                            <IconButton
                              onClick={() => handleOpenDialog(question)}
                              sx={{
                                color: "#1976d2",
                                marginRight: 1,
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Xóa">
                            <IconButton
                              onClick={() => handleDeleteClick(question)}
                              sx={{
                                color: "#ef4444",
                                "&:hover": {
                                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totalQuestions}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: "1px solid #e2e8f0",
              "& .MuiTablePagination-toolbar": {
                padding: "16px",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: "24px", color: "#1a202c" }}
        >
          {selectedQuestion ? "Chỉnh sửa câu hỏi" : "Thêm câu hỏi mới"}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="Nội dung câu hỏi (HTML)"
              name="contentQuestions"
              value={formData.contentQuestions}
              onChange={handleInputChange}
              required
              multiline
              rows={6}
            />
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  marginBottom: 1,
                }}
              >
                <TextField
                  fullWidth
                  label="URL ảnh"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                  disabled={uploadingImage}
                />
                <input
                  ref={imageUploadRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                <Button
                  variant="outlined"
                  onClick={() => imageUploadRef.current?.click()}
                  disabled={uploadingImage}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    minWidth: "120px",
                  }}
                >
                  {uploadingImage ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Upload ảnh"
                  )}
                </Button>
              </Box>
              {formData.imageUrl && (
                <Box
                  sx={{
                    marginTop: 2,
                    padding: 2,
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: 1, fontWeight: 600 }}
                  >
                    Preview ảnh:
                  </Typography>
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              )}
            </Box>
            <TextField
              fullWidth
              label="Lời giải"
              name="explanation"
              value={formData.explanation}
              onChange={handleInputChange}
              multiline
              rows={4}
            />
            <Autocomplete
              multiple
              freeSolo
              options={allTags}
              value={formData.tags}
              onChange={handleTagsChange}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Nhập tag" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", gap: 2 }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedQuestion ? (
              "Cập nhật"
            ) : (
              "Tạo mới"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmModal
        isOpen={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setSelectedQuestion(null);
          setSelectedQuestions([]);
        }}
        onConfirm={handleDeleteConfirm}
        title={
          selectedQuestions.length > 0
            ? `Xóa ${selectedQuestions.length} câu hỏi`
            : "Xóa câu hỏi"
        }
        message={
          selectedQuestions.length > 0
            ? `Bạn có chắc chắn muốn xóa ${selectedQuestions.length} câu hỏi đã chọn?`
            : "Bạn có chắc chắn muốn xóa câu hỏi này?"
        }
      />

      {/* View Question Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setViewingQuestion(null);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflowY: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            color: "#1a202c",
            borderBottom: "1px solid #e2e8f0",
            padding: "20px 24px",
          }}
        >
          Chi tiết câu hỏi
        </DialogTitle>
        <DialogContent sx={{ padding: "24px", overflowY: "auto" }}>
          {viewingQuestion && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Nội dung câu hỏi */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: 2,
                    color: "#1a202c",
                  }}
                >
                  Nội dung câu hỏi
                </Typography>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <MathRendererContent
                    content={viewingQuestion.contentQuestions || ""}
                  />
                </Box>
              </Box>

              {/* Ảnh nếu có */}
              {viewingQuestion.imageUrl && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 2,
                      color: "#1a202c",
                    }}
                  >
                    Hình ảnh
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      padding: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <img
                      src={viewingQuestion.imageUrl}
                      alt="Câu hỏi"
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Lời giải */}
              {viewingQuestion.explanation && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 2,
                      color: "#1a202c",
                    }}
                  >
                    Lời giải
                  </Typography>
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <MathRendererContent
                      content={viewingQuestion.explanation}
                    />
                  </Box>
                </Box>
              )}

              {/* Tags */}
              {viewingQuestion.tags && viewingQuestion.tags.length > 0 && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 2,
                      color: "#1a202c",
                    }}
                  >
                    Tags
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {viewingQuestion.tags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        label={tag}
                        sx={{
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Thông tin bổ sung */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", marginTop: 2 }}
                >
                  Ngày tạo:{" "}
                  {viewingQuestion.createdAt
                    ? new Date(viewingQuestion.createdAt).toLocaleString(
                        "vi-VN"
                      )
                    : "-"}
                </Typography>
                {viewingQuestion.createdBy && (
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    Người tạo: {viewingQuestion.createdBy.email || "-"}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => {
              setOpenViewDialog(false);
              setViewingQuestion(null);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
            }}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog
        open={openBulkDialog}
        onClose={() => {
          setOpenBulkDialog(false);
          setParsedQuestions([]);
          setSelectedQuestionIndex(0);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            color: "#1a202c",
            borderBottom: "1px solid #e2e8f0",
            padding: "20px 24px",
          }}
        >
          Chỉnh sửa câu hỏi ({parsedQuestions.length} câu hỏi)
        </DialogTitle>
        <DialogContent sx={{ padding: 0, display: "flex", height: "100%" }}>
          <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
            {/* Left sidebar - Question list */}
            <Box
              sx={{
                width: "300px",
                borderRight: "1px solid #e2e8f0",
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
              }}
            >
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, marginBottom: 1 }}
                >
                  Danh sách câu hỏi
                </Typography>
                {parsedQuestions.map((question, index) => (
                  <Box
                    key={index}
                    onClick={() => handleSelectQuestion(index)}
                    sx={{
                      padding: 1.5,
                      marginBottom: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        selectedQuestionIndex === index ? "#1976d2" : "#fff",
                      color: selectedQuestionIndex === index ? "#fff" : "#333",
                      border:
                        selectedQuestionIndex === index
                          ? "2px solid #1976d2"
                          : "1px solid #e2e8f0",
                      "&:hover": {
                        backgroundColor:
                          selectedQuestionIndex === index
                            ? "#1565c0"
                            : "#f0f0f0",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: selectedQuestionIndex === index ? 600 : 400,
                      }}
                    >
                      Câu hỏi {index + 1}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        marginTop: 0.5,
                        opacity: 0.8,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          question.contentQuestions?.substring(0, 50) + "..." ||
                          "Chưa có nội dung",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right side - Edit form */}
            <Box sx={{ flex: 1, padding: 3, overflowY: "auto" }}>
              {parsedQuestions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Câu hỏi {selectedQuestionIndex + 1} /{" "}
                      {parsedQuestions.length}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setOpenBulkPreviewDialog(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Xem preview
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    label="Nội dung câu hỏi (HTML)"
                    name="contentQuestions"
                    value={
                      currentEditingContentRef.current ||
                      parsedQuestions[selectedQuestionIndex]
                        ?.contentQuestions ||
                      ""
                    }
                    onChange={(e) => {
                      currentEditingContentRef.current = e.target.value;
                    }}
                    required
                    multiline
                    rows={8}
                  />

                  <TextField
                    fullWidth
                    label="Lời giải"
                    name="explanation"
                    value={
                      currentEditingExplanationRef.current ||
                      parsedQuestions[selectedQuestionIndex]?.explanation ||
                      ""
                    }
                    onChange={(e) => {
                      currentEditingExplanationRef.current = e.target.value;
                    }}
                    multiline
                    rows={6}
                  />

                  <Autocomplete
                    multiple
                    freeSolo
                    options={allTags}
                    value={parsedQuestions[selectedQuestionIndex]?.tags || []}
                    onChange={(event, newValue) => {
                      // Normalize tags to uppercase non-accent Vietnamese
                      const normalizedTags = newValue.map((tag) => {
                        if (typeof tag === "string" && tag.trim()) {
                          return toUpperCaseNonAccentVietnamese(tag.trim());
                        }
                        return tag;
                      });
                      handleQuestionChange(
                        selectedQuestionIndex,
                        "tags",
                        normalizedTags
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Nhập tag"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                        />
                      ))
                    }
                  />

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "space-between",
                      marginTop: 2,
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (selectedQuestionIndex > 0) {
                          handleSelectQuestion(selectedQuestionIndex - 1);
                        }
                      }}
                      disabled={selectedQuestionIndex === 0}
                      sx={{ textTransform: "none" }}
                    >
                      Câu trước
                    </Button>
                    <Button
                      onClick={() => {
                        if (
                          selectedQuestionIndex <
                          parsedQuestions.length - 1
                        ) {
                          handleSelectQuestion(selectedQuestionIndex + 1);
                        }
                      }}
                      disabled={
                        selectedQuestionIndex === parsedQuestions.length - 1
                      }
                      sx={{ textTransform: "none" }}
                    >
                      Câu sau
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            gap: 2,
          }}
        >
          <Button
            onClick={() => {
              setOpenBulkDialog(false);
              setParsedQuestions([]);
              setSelectedQuestionIndex(0);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmitBulk}
            variant="contained"
            disabled={loading || parsedQuestions.length === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Submit (${parsedQuestions.length} câu hỏi)`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Preview Dialog */}
      <Dialog
        open={openBulkPreviewDialog}
        onClose={() => setOpenBulkPreviewDialog(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflowY: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            color: "#1a202c",
            borderBottom: "1px solid #e2e8f0",
            padding: "20px 24px",
          }}
        >
          Preview câu hỏi {selectedQuestionIndex + 1} / {parsedQuestions.length}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px", overflowY: "auto" }}>
          {parsedQuestions.length > 0 &&
            parsedQuestions[selectedQuestionIndex] && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Nội dung câu hỏi */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 2,
                      color: "#1a202c",
                    }}
                  >
                    Nội dung câu hỏi
                  </Typography>
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <MathRendererContent
                      content={
                        currentEditingContentRef.current ||
                        parsedQuestions[selectedQuestionIndex]
                          ?.contentQuestions ||
                        ""
                      }
                    />
                  </Box>
                </Box>

                {/* Ảnh nếu có */}
                {parsedQuestions[selectedQuestionIndex]?.imageUrl && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: 2,
                        color: "#1a202c",
                      }}
                    >
                      Hình ảnh
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <img
                        src={parsedQuestions[selectedQuestionIndex].imageUrl}
                        alt="Câu hỏi"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Lời giải */}
                {(currentEditingExplanationRef.current ||
                  parsedQuestions[selectedQuestionIndex]?.explanation) && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: 2,
                        color: "#1a202c",
                      }}
                    >
                      Lời giải
                    </Typography>
                    <Box
                      sx={{
                        padding: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <MathRendererContent
                        content={
                          currentEditingExplanationRef.current ||
                          parsedQuestions[selectedQuestionIndex]?.explanation ||
                          ""
                        }
                      />
                    </Box>
                  </Box>
                )}

                {/* Tags */}
                {parsedQuestions[selectedQuestionIndex]?.tags &&
                  parsedQuestions[selectedQuestionIndex].tags.length > 0 && (
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          marginBottom: 2,
                          color: "#1a202c",
                        }}
                      >
                        Tags
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {parsedQuestions[selectedQuestionIndex].tags.map(
                          (tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              sx={{
                                fontWeight: 500,
                              }}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  )}
              </Box>
            )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => setOpenBulkPreviewDialog(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
            }}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionBankManagement;
